/**
 * Listing page snapshot · last `cars.listWithTotal` payload for a filter key.
 * Survives CarListings unmount so returning to جميع المركبات does not flash GridSkeleton.
 */

export type ListingPageListInput = Record<string, unknown>;

export type ListingPageSnapshot<TItem = unknown> = {
  items: TItem[];
  total: number;
};

export const DEFAULT_LISTINGS_LIST_INPUT = { limit: 12, offset: 0 } as const;

export type ListingsPrefetchInput = {
  limit: number;
  offset: number;
  saleType?: "auction" | "fixed";
};

/** Default `cars.listWithTotal` input for a listings nav href. */
export function listingsPrefetchInput(href: string): ListingsPrefetchInput {
  if (href.includes("type=auction")) {
    return { ...DEFAULT_LISTINGS_LIST_INPUT, saleType: "auction" };
  }
  if (href.includes("type=fixed")) {
    return { ...DEFAULT_LISTINGS_LIST_INPUT, saleType: "fixed" };
  }
  return { ...DEFAULT_LISTINGS_LIST_INPUT };
}

export function omitUndefinedKeys(input: ListingPageListInput): ListingPageListInput {
  const out: ListingPageListInput = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) out[key] = value;
  }
  return out;
}

export function listingPageSnapshotKey(input: ListingPageListInput): string {
  return JSON.stringify(omitUndefinedKeys(input));
}

/** Filter identity for placeholder reuse · page/limit may differ, saleType may not. */
export function listingFilterScopeKey(input: ListingPageListInput): string {
  const clean = omitUndefinedKeys(input);
  const { limit: _limit, offset: _offset, ...scope } = clean;
  return JSON.stringify(scope);
}

export function sameListingFilterScope(
  previousInput: ListingPageListInput,
  nextInput: ListingPageListInput,
): boolean {
  return listingFilterScopeKey(previousInput) === listingFilterScopeKey(nextInput);
}

export function listingInputFromQueryKey(queryKey: unknown): ListingPageListInput | undefined {
  if (!Array.isArray(queryKey)) return undefined;
  for (const part of queryKey) {
    if (!part || typeof part !== "object") continue;
    const input = (part as { input?: unknown }).input;
    if (input && typeof input === "object") return input as ListingPageListInput;
  }
  return undefined;
}

const snapshots = new Map<string, ListingPageSnapshot>();

export function clearListingPageSnapshots(): void {
  snapshots.clear();
}

export function rememberListingPage<TItem>(
  input: ListingPageListInput,
  data: ListingPageSnapshot<TItem>,
): void {
  snapshots.set(listingPageSnapshotKey(input), data as ListingPageSnapshot);
}

export function recallListingPage<TItem>(
  input: ListingPageListInput,
): ListingPageSnapshot<TItem> | undefined {
  return snapshots.get(listingPageSnapshotKey(input)) as ListingPageSnapshot<TItem> | undefined;
}

/** React Query `placeholderData` · keep previous only inside the same filter scope. */
export function listingPagePlaceholderData<T extends ListingPageSnapshot>(
  previous: T | undefined,
  input: ListingPageListInput,
  previousInput?: ListingPageListInput,
): T | undefined {
  if (previous && previousInput && sameListingFilterScope(previousInput, input)) {
    return previous;
  }
  return recallListingPage(input) as T | undefined;
}

export function resolveListingGridView<TItem>(args: {
  input: ListingPageListInput;
  queryItems: TItem[] | undefined;
  isPending: boolean;
}): { items: TItem[] | undefined; showPlaceholders: boolean } {
  const snapshot = recallListingPage<TItem>(args.input);
  const items = args.queryItems ?? snapshot?.items;
  return {
    items,
    showPlaceholders: args.isPending && items === undefined,
  };
}
